import React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'core/components/button';
import { Tooltip } from 'core/components/tooltip';
import ignoreIcon from 'core/assets/svg/ic-ignore.svg';
import ignoreGreyIcon from 'core/assets/svg/ic-ignore-grey.svg';
import helpCircleIcon from 'core/assets/svg/help-circle.svg';
import { dateFormatter } from 'core/helpers/formatters';
import { IncidentStatusTag, actionLabel, incidentStatusTooltip } from 'common/components/ignore-incidents';
import { IncidentStatus, incidentStatusLabels } from 'common/helpers/ignored-incidents';
import { getSourceLabel, getViolationTitle } from './helpers';
import useStyles from './factors-impacting-price.styles';

const isIgnored = (incident) =>
  incident.status === IncidentStatus.Ignored || incident.status === IncidentStatus.IgnoredByPriorCarrier;

// A driver's claims and violations, grouped by date (#36871). Laid out like the offer's claim dispute rows:
// an action on each row, and a grey badge on the ones that are ignored.
const PersonIncidents = ({ incidents, canIgnore, onIgnore, onStopIgnoring }) => {
  const classes = useStyles();
  const dates = [...new Set(incidents.map((incident) => incident.date))];

  const details = (incident) => (
    <div className={classes.incidentText}>
      <span className={classes.factorViolationTitle}>{getViolationTitle(incident.type)}</span>
      <span className={classes.factorMetadata}>
        Source: {getSourceLabel({ source: incident.source, isCourtRecord: incident.isCourtRecord })}
      </span>
    </div>
  );

  const ignoredRow = (incident) => {
    const tooltip = incidentStatusTooltip(incident);
    return (
      <div key={incident.id} className={classes.ignoredRow}>
        <div className={classes.ignoredBadge}>
          <img src={ignoreGreyIcon} alt="" className={classes.actionIcon} />
          <span className={classes.ignoredBadgeLabel}>{incidentStatusLabels[incident.status]}</span>
          {tooltip && <Tooltip text={tooltip} placement="top" smallIcon={{ src: helpCircleIcon }} iconSize="icon16" />}
        </div>
        <div className={classes.ignoredDivider} />
        {details(incident)}
        {canIgnore && incident.status === IncidentStatus.Ignored && (
          <Button
            variant="text"
            color="secondary"
            className={classes.rowAction}
            aria-label={actionLabel('Stop ignoring', incident)}
            onClick={() => onStopIgnoring(incident)}
          >
            Stop ignoring
          </Button>
        )}
      </div>
    );
  };

  return dates.map((date) => (
    <div key={date} className={classes.incidentDateGroup}>
      <p className={classes.incidentDate}>{dateFormatter(date)}</p>
      {incidents
        .filter((incident) => incident.date === date)
        .map((incident) =>
          isIgnored(incident) ? (
            ignoredRow(incident)
          ) : (
            <div key={incident.id} className={classes.incidentRow}>
              {details(incident)}
              <IncidentStatusTag incident={incident} />
              {canIgnore && incident.canIgnore && (
                <Button
                  variant="text"
                  color="secondary"
                  className={classes.rowAction}
                  aria-label={actionLabel('Ignore', incident)}
                  onClick={() => onIgnore(incident)}
                >
                  <img src={ignoreIcon} alt="" className={classes.actionIcon} />
                  Ignore
                </Button>
              )}
            </div>
          )
        )}
    </div>
  ));
};

PersonIncidents.propTypes = {
  incidents: PropTypes.array.isRequired,
  canIgnore: PropTypes.bool.isRequired,
  onIgnore: PropTypes.func.isRequired,
  onStopIgnoring: PropTypes.func.isRequired
};

export default PersonIncidents;
